<script setup lang="ts">
import {
  getCategoryRoute,
  getTranslatedProperty,
  getCategoryImageUrl,
} from "@shopware/helpers";
import type { Schemas } from "#shopware";
type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const props = defineProps<{
  navigationElementChildren: Array<NavigationElement>;
  isMobileMenuOpen?: boolean;
}>();

const emits = defineEmits<{
  (
    e: "updateActiveClass",
    navigationId: string,
    parentId: string | undefined
  ): void;
  (e: "focusoutLastElement", lastElement: boolean): void;
}>();

const emitUpdateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  emits("updateActiveClass", navigationId, parentId);
};

// Track which items are expanded
const expandedItems = ref<Set<string>>(new Set());

const toggleExpanded = (itemId: string) => {
  if (expandedItems.value.has(itemId)) {
    expandedItems.value.delete(itemId);
  } else {
    expandedItems.value.add(itemId);
  }
};

const isExpanded = (itemId: string) => {
  return expandedItems.value.has(itemId);
};
</script>

<template>
  <template
    v-for="(childElement, index) in navigationElementChildren.sort((a, b) => {
      const aHasChildren = a.children && a.children.length > 0;
      const bHasChildren = b.children && b.children.length > 0;
      if (aHasChildren && !bHasChildren) return -1;
      if (!aHasChildren && bHasChildren) return 1;
      return 0;
    })"
    :key="childElement.id"
  >
    <!-- Items without subcategories (regular li elements) -->
    <li
      v-if="
        !childElement.translated?.customFields?.custom_new_and_trendy_enabled &&
        (!childElement.children || childElement.children.length === 0)
      "
      class="mb-2 menu-line order-2"
    >
      <div
        class="justify-between category-link uppercase cursor-pointer flex items-center gap-2 w-full font-bold"
      >
        <!-- If item has no children, show as normal link -->
        <div
          class="bg-black03 w-full text-center font-bold"
          :class="{
            '!bg-red-500 !text-white':
              getTranslatedProperty(childElement, 'name') === 'Sale',
          }"
        >
          <!-- Show category image if available -->
          <div v-if="childElement.media" class="mb-0">
            <NuxtLink
              :to="formatLink(getCategoryRoute(childElement))"
              :target="
                childElement.externalLink || childElement.linkNewTab
                  ? '_blank'
                  : ''
              "
              @click="
                emitUpdateActiveClass(childElement.id, childElement.parentId)
              "
            >
              <img
                :src="getCategoryImageUrl(childElement)"
                class="w-full h-[100px] object-cover object-center"
                :alt="getTranslatedProperty(childElement, 'name')"
              />
            </NuxtLink>
          </div>
          <!-- Fallback placeholder if no image -->
          <div v-else class="h-[100px]"></div>
          <NuxtLink
            :to="formatLink(getCategoryRoute(childElement))"
            :target="
              childElement.externalLink || childElement.linkNewTab
                ? '_blank'
                : ''
            "
            :class="{
              'text-pink font-semibold': childElement.activeClass,
              '!bg-red-500 text-white':
                getTranslatedProperty(childElement, 'name') === 'Sale',
            }"
            class="uppercase text-left text-white font-bold px-4 py-3 w-full flex items-center justify-start text-[14px]"
            @click="
              emitUpdateActiveClass(childElement.id, childElement.parentId)
            "
          >
            {{ getTranslatedProperty(childElement, "name") }}
          </NuxtLink>
        </div>
      </div>
    </li>
  </template>

  <!-- Single div wrapper for all items with subcategories -->
  <div
    v-if="navigationElementChildren.some(child => 
      !child.translated?.customFields?.custom_new_and_trendy_enabled &&
      child.children && 
      child.children.length > 0
    )"
    class="subcategories-container order-1 flex flex-wrap w-full"
  >
    <li
      v-for="childElement in navigationElementChildren.filter(child => 
        !child.translated?.customFields?.custom_new_and_trendy_enabled &&
        child.children && 
        child.children.length > 0
      )"
      :key="childElement.id"
      class="mb-2 menu-line"
    >
      <div
        class="justify-between category-link uppercase cursor-pointer flex items-center gap-2 w-full font-bold"
      >
        <!-- If item has children, show as non-clickable text with expand functionality -->
        <div class="py-2 px-4 flex items-center gap-3 w-full">
          <span
            class="bg-none uppercase border-none cursor-pointer text-left text-sm text-inherit p-0 transition-colors duration-200"
            :class="{
              'text-pink font-semibold': childElement.activeClass,
              '!bg-red-500 text-white':
                getTranslatedProperty(childElement, 'name') === 'SALE',
            }"
            @click="toggleExpanded(childElement.id)"
          >
            {{ getTranslatedProperty(childElement, "name") }}
          </span>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="{ 'rotate-180': isExpanded(childElement.id) }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
      <ul
        class="ml-0 border-l border-gray-200 fixed bg-white top-[100px] left-0 m-0 p-0 h-full w-full"
        :class="isExpanded(childElement.id) ? 'block' : 'hidden'"
      >
        <!-- Back Button and Header -->
        <div
          class="flex items-center gap-2 px-4 py-3 bg-gray-100 border-b border-gray-200 cursor-pointer font-medium text-gray-600 hover:bg-gray-200 transition-colors duration-200"
          @click="toggleExpanded(childElement.id)"
        >
          <i class="fa fa-angle-left text-base"></i> Zurück
        </div>

        <!-- Subcategory Content -->
        <div
          class="overflow-y-auto h-[calc(100vh-180px)] pt-5 px-4 [&_li>div>div>div]:hidden [&_li>div>div]:bg-white [&_li>div>div>a]:text-black03 [&_li>div>div>a]:py-2 [&_li>div>div>a]:px-0 [&_li>div>div>a]:capitalize [&_li>div>div>a]:text-[16px] [&_li>div>div>a]:font-medium"
        >
          <h2 class="text-[16px] font-semibold text-gray-900 px-1">
            {{ getTranslatedProperty(childElement, "name") }}
          </h2>
          <LayoutTopNavigationRecursiveMobile
            :navigation-element-children="childElement.children"
            @update-active-class="
              emitUpdateActiveClass(childElement.id, childElement.parentId)
            "
          />
        </div>
      </ul>
    </li>
  </div>
</template>

<style scoped>
/* All styles converted to Tailwind classes */
</style>
